/* eslint-disable max-len */
/* eslint-react/no-danger */
/* eslint-disable no-plusplus */
import React, { useEffect, useContext } from 'react';
import {
  string,
  number,
  arrayOf,
  shape,
  element,
  any,
  bool,
  func,
  oneOf,
} from 'prop-types';
import { useVariationIds } from '@thd-olt-component-react/component-selector';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classnames from 'classnames';
import { componentsWithDefaultAspectRatios } from '../ComponentDictionaryEntries/Entries';
import { getHydrationOptions } from './utils';
import styles from './layout-renderer.module.scss';
import useLayoutOrchestration from '../hooks/useLayoutOrchestration';

function renderComponent(ComponentName, componentData, components) {
  if (!components[ComponentName]) {
    return null;
  }

  const Component = components[ComponentName];
  /* eslint-disable react/jsx-props-no-spreading */
  return <Component {...componentData} />;
}

function getComponentPropsData(data, components, componentSelectorData) {
  const dataCopy = { ...data };

  delete dataCopy.sizes;
  delete dataCopy.originalPosition;
  delete dataCopy.styles;

  dataCopy.children = dataCopy?.children?.map(
    (child, layoutRendererPrerenderedChildIndex) => {
      const renderId = child.customRenderId || child.__typename;

      if (!components[renderId]) {
        return null;
      }

      const childCopy = { ...child };

      delete childCopy.sizes;
      delete childCopy.originalPosition;
      delete childCopy.styles;

      childCopy.layoutRendererPrerenderedChildIndex = layoutRendererPrerenderedChildIndex;

      if (childCopy.__typename === 'Image' && !childCopy.alt) {
        childCopy.alt = '';
      }

      if (childCopy?.customRenderId === 'ComponentSelector') {
        childCopy.variationIds = componentSelectorData.variationIds;
        childCopy.renderDefault = componentSelectorData.renderDefault;
        childCopy.mboxes = componentSelectorData.mboxes;
      }

      if (dataCopy.customRenderId in componentsWithDefaultAspectRatios) {
        childCopy.useDefaultAspectRatio = true;
      }

      if (dataCopy.customRenderId === 'B2B Marketing Gallery') {
        childCopy.useB2bStyles = true;
      }

      childCopy.disableAspectRatio = true;

      const Component = components[renderId];

      /* eslint-disable react/jsx-props-no-spreading */
      return <Component {...childCopy} />;
    },
  );

  return dataCopy;
}

/* eslint-disable react/prop-types */
const Layout = ({
  components,
  layoutData,
  componentSelectorData,
  loading,
  channel,
  LoadingLayout,
  customerType,
}) => {

  const hydrationOptions = getHydrationOptions(channel);
  function buildComponent(layoutComponent, index) {
    const componentProps = getComponentPropsData(
      layoutComponent,
      components,
      componentSelectorData,
    );

    const Component = renderComponent(
      componentProps.__typename,
      componentProps,
      components,
    );

    const componentId = layoutComponent?.id;

    if (!Component) {
      return null;
    }
    const { minWidthMedium, maxWidthMedium, minWidthLarge } = layoutComponent.renderWidths;
    const maxWidthMediumSpan = classnames({
      'sui-col-span-1': maxWidthMedium === 1,
      'sui-col-span-2': maxWidthMedium === 2,
      'sui-col-span-3': maxWidthMedium === 3,
      'sui-col-span-4': maxWidthMedium === 4,
      'sui-col-span-5': maxWidthMedium === 5,
      'sui-col-span-6': maxWidthMedium === 6,
      'sui-col-span-7': maxWidthMedium === 7,
      'sui-col-span-8': maxWidthMedium === 8,
      'sui-col-span-9': maxWidthMedium === 9,
      'sui-col-span-10': maxWidthMedium === 10,
      'sui-col-span-11': maxWidthMedium === 11,
      'sui-col-span-12': maxWidthMedium === 12,
    });

    const minWidthMediumSpan = classnames({
      'md:sui-col-span-1': minWidthMedium === 1,
      'md:sui-col-span-2': minWidthMedium === 2,
      'md:sui-col-span-3': minWidthMedium === 3,
      'md:sui-col-span-4': minWidthMedium === 4,
      'md:sui-col-span-5': minWidthMedium === 5,
      'md:sui-col-span-6': minWidthMedium === 6,
      'md:sui-col-span-7': minWidthMedium === 7,
      'md:sui-col-span-8': minWidthMedium === 8,
      'md:sui-col-span-9': minWidthMedium === 9,
      'md:sui-col-span-10': minWidthMedium === 10,
      'md:sui-col-span-11': minWidthMedium === 11,
      'md:sui-col-span-12': minWidthMedium === 12,
    });

    const minWidthLargeSpan = classnames({
      'lg:sui-col-span-1': minWidthLarge === 1,
      'lg:sui-col-span-2': minWidthLarge === 2,
      'lg:sui-col-span-3': minWidthLarge === 3,
      'lg:sui-col-span-4': minWidthLarge === 4,
      'lg:sui-col-span-5': minWidthLarge === 5,
      'lg:sui-col-span-6': minWidthLarge === 6,
      'lg:sui-col-span-7': minWidthLarge === 7,
      'lg:sui-col-span-8': minWidthLarge === 8,
      'lg:sui-col-span-9': minWidthLarge === 9,
      'lg:sui-col-span-10': minWidthLarge === 10,
      'lg:sui-col-span-11': minWidthLarge === 11,
      'lg:sui-col-span-12': minWidthLarge === 12,
    });

    return (
      <React.Fragment key={index}>
        {index <= 6 ? (
          <div
            className={`${maxWidthMediumSpan} ${minWidthMediumSpan} ${minWidthLargeSpan}`}
            key={componentId}
          >
            {Component}
          </div>
        ) : (
          <div
            className={`${maxWidthMediumSpan} ${minWidthMediumSpan} ${minWidthLargeSpan}`}
            key={componentId}
          >
            <Hydrator
              id={`${componentId}-php-hydrator-${index}`}
              className={styles['u-height-100']}
              scrollBuffer={hydrationOptions.SCROLL_BUFFER}
              delay={hydrationOptions.HOMEPAGE_DELAY_STANDARD}
              placeholder={hydrationOptions.PLACEHOLDER}
            >
              {Component}
            </Hydrator>
          </div>
        )}
      </React.Fragment>
    );
  }
  const heroCarouselIndex = layoutData.components.findIndex((component) => component.customRenderId === 'Hero Carousel');
  const heroCarousel = layoutData.components[heroCarouselIndex];

  return layoutData.components.length > 0 ? (
    <>
      {buildComponent(heroCarousel, heroCarouselIndex)}
      <div className="sui-grid sui-grid-flow-row-dense sui-gap-4 sui-grid-cols-12 sui-mx-4 lg:sui-mx-0">
        {layoutData.components.map((layoutComponent, index) => {
          if (!layoutComponent || layoutComponent.customRenderId === 'Hero Carousel') return null;

          return buildComponent(layoutComponent, index);
        })}
      </div>
      {loading.server || loading.client ? LoadingLayout : null}
    </>
  ) : null;
};

function selectMetadata(data, ssr) {
  return data?.seo && ssr ? data.seo : undefined;
}

const LayoutRenderer = ({
  layoutOperations,
  componentOptions,
  components,
  layoutDataModel,
  contentfulPreviewId,
  layoutClass,
  slug,
  loadingLayout: LoadingLayout,
  onContentInformationApiError,
  ssr,
  renderMetadata,
  customerType,
}) => {
  let variables;
  if (contentfulPreviewId) {
    variables = {
      id: contentfulPreviewId,
      layoutClass,
    };
  } else {
    variables = {
      slug,
    };
  }

  const layoutOptions = {
    componentOptions,
    myComponents: components,
  };

  if (layoutOperations?.length > 0) {
    layoutOptions.layoutOperations = layoutOperations;
  }

  const {
    data: layoutData,
    loading,
    error,
  } = useLayoutOrchestration({
    variables,
    layoutDataModel,
    layoutOptions,
    ssr,
    customerType,
  });

  const { channel } = useContext(ExperienceContext);

  const mboxes = layoutData?.mboxes || [];
  const campaignIds = layoutData?.campaignIds || [];
  const isReady = layoutData?.isReady;

  const { variationIds, renderDefault } = useVariationIds({
    mboxes,
    campaignIds,
    isReady,
    channel,
  });

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('layout-renderer.ready');
  }, []);

  if (error.server || error.client) {
    return onContentInformationApiError(error);
  }

  let heroCarouselData;

  if (layoutData?.components) {
    for (let i = 0; i < layoutData.components.length; i++) {
      if (layoutData.components[i].customRenderId === 'Hero Carousel') {
        heroCarouselData = layoutData.components[i];
      }
    }
  }

  return (
    <>
      {layoutData?.seo && ssr
        ? renderMetadata(
          selectMetadata(layoutData, ssr),
          slug,
          heroCarouselData,
        )
        : null}
      <div className={styles['layout-renderer']}>
        {layoutData ? (
          <Layout
            components={components}
            layoutData={layoutData}
            componentSelectorData={{
              variationIds,
              renderDefault,
              mboxes: layoutData?.mboxes,
            }}
            channel={channel}
            loading={loading}
            LoadingLayout={LoadingLayout}
            customerType={customerType}
          />
        ) : null}
      </div>
    </>
  );
};
LayoutRenderer.propTypes = {
  components: shape({
    [string]: element,
  }).isRequired,
  layoutOperations: arrayOf(string),
  componentOptions: shape({
    [string]: shape({
      locked: shape({
        xPos: number,
        yPos: number,
      }),
      sizes: shape({
        minWidthMedium: {
          min: number,
          preferred: number,
          max: number,
        },
        minWidthLarge: {
          min: number,
          preferred: number,
          max: number,
        },
      }),
    }),
  }),
  slug: string,
  // eslint-disable-next-line react/forbid-prop-types
  layoutDataModel: any,
  loadingLayout: element,
  contentfulPreviewId: string,
  // eslint-disable-next-line react/forbid-prop-types
  layoutClass: oneOf(['PersonalizedHomePage', 'EducationalLayout']),
  onContentInformationApiError: func,
  ssr: bool,
  renderMetadata: func,
};

LayoutRenderer.defaultProps = {
  layoutOperations: [],
  componentOptions: {},
  layoutDataModel: {},
  slug: null,
  contentfulPreviewId: null,
  layoutClass: null,
  loadingLayout: null,
  onContentInformationApiError: () => null,
  ssr: false,
  renderMetadata: () => null,
};

LayoutRenderer.displayName = 'LayoutRenderer';

export { LayoutRenderer, selectMetadata };
